import { useEffect, useRef } from 'react';

interface KeyboardShortcutOptions {
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
}

type ShortcutHandlers = Record<string, (event: KeyboardEvent) => void>;

/**
 * Hook for registering keyboard shortcuts
 * @param handlers Map of key to handler function
 * @param options Modifier key requirements
 */
export function useKeyboardShortcuts(
  handlers: ShortcutHandlers,
  options: KeyboardShortcutOptions = {}
) {
  // Store handlers in ref to avoid re-registration
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const { ctrl, shift, alt, meta } = options;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if modifiers match
      if (ctrl !== undefined && event.ctrlKey !== ctrl) return;
      if (shift !== undefined && event.shiftKey !== shift) return;
      if (alt !== undefined && event.altKey !== alt) return;
      if (meta !== undefined && event.metaKey !== meta) return;

      // Find matching handler
      const handler = handlersRef.current[event.key];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ctrl, shift, alt, meta]);
}
